<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row" v-if="userData.permissions['view_doctor'] == 1">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row g-2 align-items-end mb-3 queue-filter-row">
              <div class="col-md-3">
                <label class="form-label">Date From</label>
                <input v-model="datefrom" type="date" class="form-control" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Date To</label>
                <input v-model="dateto" type="date" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label">Search Patient</label>
                <div class="input-group">
                  <b-form-input
                    v-model="search"
                    type="search"
                    placeholder="Name, patient ID, or telephone"
                    class="form-control"
                    @keyup.enter="applyFilters"
                  ></b-form-input>
                  <b-button variant="primary" @click="applyFilters" :disabled="loading">
                    <span v-if="loading"><b-spinner small></b-spinner></span>
                    <span v-else><i class="mdi mdi-magnify"></i></span>
                  </b-button>
                </div>
              </div>
              <div class="col-md-2">
                <label class="form-label">Rows Per Page</label>
                <div class="rows-select-wrap">
                  <span class="rows-select-icon"><i class="mdi mdi-format-list-numbered"></i></span>
                  <b-form-select v-model="perPage" :options="options" class="rows-select"></b-form-select>
                </div>
              </div>
            </div>

          <div class="queue-table-wrap">
              <b-table
                :items="itemsList"
                :fields="fields"
                :busy="loading"
                class="queue-table"
                show-empty
                empty-text="No patients found for the selected filters."
                tbody-tr-class="queue-table-row"
                @row-clicked="goToRowAction"
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle" type="grow" small></b-spinner> <strong>Loading Patients...</strong>
                  </div>
                </template>
                <template #empty>
                  <div class="text-center text-muted py-4">
                    <i class="mdi mdi-folder-search-outline d-block mb-2" style="font-size: 2rem;"></i>
                    <strong>No patients found.</strong>
                    <div class="small mt-1">Try a different date range or search term.</div>
                  </div>
                </template>
                <template #cell(name)="data">
                  <div class="cell-name">
                    <b class="cell-name-primary">{{ data.item.patient.name }}</b>
                    <small class="cell-name-secondary">0{{ data.item.patient.telephone }}</small>
                  </div>
                  <span v-if="data.item.traige && data.item.traige.turnaroundtime == 2">
                    <i class="bx bx-flashing"><span class="badge bg-danger rounded-pill">emergency</span></i>
                  </span>
                </template>
                <template #cell(gender)="data">
                  <div class="cell-gender">
                    <b>{{ data.item.patientage }}</b>
                    <span>{{ data.item.patient.gender }}</span>
                  </div>
                </template>
                <template #cell(service)="data">
                  <span
                    v-if="data.item.patientvisit && data.item.patientvisit.payment_type != 4"
                    class="wrap-text"
                    :title="data.item.service_name ? data.item.service_name.name : ''"
                  >
                    {{ data.item.service_name ? data.item.service_name.name : '' }}
                  </span>
                  <span
                    v-else
                    class="wrap-text"
                    :title="data.item.insurance_service_name ? data.item.insurance_service_name.name : ''"
                  >
                    {{ data.item.insurance_service_name ? data.item.insurance_service_name.name : '' }}
                  </span>
                </template>
                <template #cell(addedon)="data">
                  <div class="visit-date-cell">
                    <div class="visit-date-primary">{{ formatVisitDate(data.item.addedon) }}</div>
                    <small class="visit-date-secondary">{{ formatVisitTime(data.item.addedon) }}</small>
                  </div>
                </template>
                <template #cell(status)="data">
                  <div class="status-cell">
                    <span v-if="data.item.paystatus > 0"><b-button class="btn btn-info btn-sm queue-status-btn" type="button">Paid</b-button></span>
                    <span v-else-if="data.item.patientvisit && data.item.patientvisit.payment_type == 4"><b-button class="btn btn-secondary btn-sm queue-status-btn" type="button">Insured</b-button></span>
                    <span v-else><b-button class="btn btn-danger btn-sm queue-status-btn" type="button">Not Paid</b-button></span>
                    <b v-if="isWorkedOn(data.item)" class="text-primary queue-status-note">Worked On</b>
                    <b v-else class="text-danger queue-status-note">Not Worked On</b>
                  </div>
                </template>
                <template #cell(actions)="data">
                  <router-link
                    :to="buildWorkRoute(data.item)"
                    type="button"
                    class="btn btn-sm btn-primary queue-action-btn"
                    @click.native.stop
                  >
                    <i class="mdi mdi-square-edit-outline"></i> Treatment
                  </router-link>
                </template>
              </b-table>
            </div>

            <div class="row">
              <div class="col">
                <div class="queue-footer">
                  <div class="queue-footer-summary">
                    <span v-if="meta.total">Showing {{ meta.from || 0 }} - {{ meta.to || 0 }} of {{ meta.total }} patients</span>
                    <span v-else>No records available</span>
                    <span v-if="meta.last_page > 1" class="queue-footer-page-note">
                      Page {{ meta.current_page }} of {{ meta.last_page }}
                    </span>
                  </div>
                  <div class="queue-footer-pagination" :class="{ 'queue-footer-pagination--active': meta.last_page > 1 }">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="meta.total"
                      :per-page="perPage"
                      :limit="7"
                      pills
                      first-number
                      last-number
                      align="right"
                      prev-text="Prev"
                      next-text="Next"
                    ></b-pagination>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="row justify-content-center">
        <div class="col-md-8 col-xl-6">
          <div>
            <img src="@/assets/images/profile-img.png" alt class="img-fluid" /><br /><br />
            <center><span class="text-primary h6">You have no permission to view doctor patients</span></center>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";

export default {
  page: {
    title: "My Patients",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, PageHeader },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      title: "My Patients",
      items: [{ text: "Dashboard", to: "/" }, { text: "My Patients", active: true }],
      loading: false,
      patients: [],
      perPage: 20,
      currentPage: 1,
      options: [10, 20, 50, 100],
      search: "",
      datefrom: new Date(new Date().setMonth(new Date().getMonth() - 6)).toLocaleDateString("fr-CA"),
      dateto: new Date().toLocaleDateString("fr-CA"),
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
        from: 0,
        to: 0,
      },
      fields: [
        { key: "name", label: "Name", thStyle: { width: "150px" } },
        { key: "gender", label: "Gender", thStyle: { width: "78px" } },
        { key: "service", label: "Service", thStyle: { width: "170px" } },
        { key: "addedon", label: "Visit Date", thStyle: { width: "120px" } },
        { key: "status", label: "Status", thStyle: { width: "120px" } },
        { key: "actions", label: "Actions", thStyle: { width: "92px" } },
      ],
    };
  },
  computed: {
    itemsList() {
      return this.patients.map((item) => ({ ...item }));
    },
  },
  async mounted() {
    await this.fetchMyPatients();
  },
  watch: {
    currentPage() {
      this.fetchMyPatients();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchMyPatients();
    },
  },
  methods: {
    async fetchMyPatients() {
      this.loading = true;
      const params = {
        page: this.currentPage,
        per_page: this.perPage,
        datefrom: this.datefrom,
        dateto: this.dateto,
        search: this.search,
      };

      await axios
        .get("/doctor-mypatients", { params })
        .then((response) => {
          this.patients = response.data.visits || [];
          this.meta = {
            ...this.meta,
            ...(response.data.meta || {}),
          };
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchMyPatients();
    },
    formatVisitDate(value) {
      if (!value) return "";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return value;
      }
      return parsed.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    goToRowAction(item) {
      const route = this.buildWorkRoute(item);
      if (route) {
        this.$router.push(route);
      }
    },
    formatVisitTime(value) {
      if (!value) return "";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return "";
      }
      return parsed.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    isWorkedOn(item) {
      if (item.service_category?.id === 3) {
        return !!item.scan;
      }
      if (item.service_category?.id === 4) {
        return !!item.lab;
      }
      if (item.service_category?.id === 7) {
        return !!item.labouroutcome;
      }
      return !!item.treatment;
    },
    buildWorkRoute(item) {
      const serviceCategoryId = Number(item.service_category?.id || 0);
      let path = "/treatment";

      if (serviceCategoryId === 3) {
        path = "/radiologyreport";
      } else if (serviceCategoryId === 6) {
        path = "/antenataltreatment";
      } else if (serviceCategoryId === 7) {
        path = "/maternitytreatment";
      } else if (serviceCategoryId === 8) {
        path = "/theatertreatment";
      }

      return { path, query: { id: item.id } };
    },
  },
};
</script>

<style scoped>
.queue-filter-row :deep(.form-label) {
  margin-bottom: 0.35rem;
  font-size: 0.82rem;
}

.queue-filter-row :deep(.form-control),
.queue-filter-row :deep(.custom-select) {
  min-height: 36px;
  font-size: 0.86rem;
}

.queue-table {
  min-width: 1080px;
  font-size: 0.9rem;
}

.queue-table :deep(th),
.queue-table :deep(td) {
  vertical-align: top;
  padding: 0.75rem 0.65rem;
}

.queue-table :deep(th) {
  white-space: nowrap;
  font-size: 0.8rem;
}

.queue-table :deep(td) {
  word-wrap: break-word;
}

.queue-table-wrap {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 118, 110, 0.28) transparent;
}

.queue-table-wrap :deep(table) {
  min-width: 1080px;
}

.queue-table-wrap::-webkit-scrollbar {
  height: 3px;
}

.queue-table-wrap::-webkit-scrollbar-track {
  background: transparent;
}

.queue-table-wrap::-webkit-scrollbar-thumb {
  background: rgba(15, 118, 110, 0.28);
  border-radius: 999px;
}

.queue-table :deep(tbody tr) {
  cursor: pointer;
  transition: background-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.queue-table :deep(tbody tr:hover) {
  background: #f8fbff;
  box-shadow: inset 0 0 0 1px rgba(15, 118, 110, 0.14);
  transform: translateY(-1px);
}

.rows-select-wrap {
  position: relative;
}

.rows-select-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #0f766e;
  z-index: 1;
  pointer-events: none;
}

.rows-select {
  padding-left: 2.3rem;
  min-height: 36px;
  border-radius: 0.65rem;
  border: 1px solid #d7e0ea;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  font-weight: 600;
  font-size: 0.86rem;
}

.cell-id,
.cell-telephone {
  white-space: nowrap;
  font-size: 0.88rem;
}

.cell-name {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.cell-name-primary {
  display: block;
  font-size: 0.95rem;
  line-height: 1.2;
}

.cell-name-secondary {
  color: #64748b;
  font-size: 0.8rem;
}

.cell-gender {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  line-height: 1.2;
}

.wrap-text {
  display: inline-block;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
}

.visit-date-cell {
  min-width: 96px;
}

.visit-date-primary {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
}

.visit-date-secondary {
  color: #64748b;
  font-size: 0.78rem;
}

.status-cell {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.queue-status-btn {
  padding: 0.2rem 0.55rem;
  font-size: 0.76rem;
  line-height: 1.2;
}

.queue-status-note {
  line-height: 1.2;
  font-size: 0.8rem;
}

.queue-action-btn {
  padding: 0.28rem 0.55rem;
  font-size: 0.76rem;
  white-space: nowrap;
}

.queue-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.queue-footer-summary {
  color: #475569;
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.queue-footer-page-note {
  color: #0f766e;
  font-weight: 600;
}

.queue-footer-pagination :deep(.pagination) {
  margin-bottom: 0;
  gap: 0.35rem;
}

.queue-footer-pagination :deep(.page-item .page-link) {
  min-width: 34px;
  height: 34px;
  padding: 0 0.55rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.65rem;
  border: 1px solid #d7e0ea;
  color: #334155;
  background: #fff;
  font-weight: 600;
  font-size: 0.78rem;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.04);
}

.queue-footer-pagination :deep(.page-item.active .page-link) {
  background: linear-gradient(135deg, #0f766e, #0d9488);
  border-color: #0f766e;
  color: #fff;
  box-shadow: 0 10px 24px rgba(15, 118, 110, 0.28);
}

.queue-footer-pagination :deep(.page-item.disabled .page-link) {
  background: #f8fafc;
  color: #94a3b8;
  box-shadow: none;
}

@media (max-width: 767.98px) {
  .queue-footer {
    align-items: stretch;
  }

  .queue-footer-pagination {
    width: 100%;
  }

  .queue-footer-pagination :deep(.pagination) {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
</style>
